import { Badge, Box, Button, DrawerBody, DrawerFooter, FormControl, FormHelperText, FormLabel, Input, Radio, RadioGroup, Stack, Switch, Tab, TabList, TabPanel, TabPanels, Tabs, useToast } from "@chakra-ui/react";
import { AuthContext } from "contexts/AuthContext";
import { useBroadcast } from "hooks/useBroadcast";
import { useContent } from "hooks/useContent";
import ResolutionPicker from "components/resolution/ResolutionPicker";
import { useGroup } from "hooks/useGroup";
import { useLocation } from "hooks/useLocation";
import { create, createBroadcastRecord, save } from "lib/firebase";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import LastSeen from "components/status/LastSeen";
import { useGoogleMap } from '@react-google-maps/api';
import ContentSelection from "components/contentSelection/contentSelection";
import BroadcastSelection from "components/broadcastSelection/broadcastSelection";
import GroupForm from "components/groupForm/groupForm";
import SearchSelect from "components/select/SearchSelect";
import { locationTemplate } from "lib/membership";
import { isCompositeBroadcast } from "lib/playlist";
import { DrawerContext } from "components/drawer";

const geocoder = new window.google.maps.Geocoder();

// The kind of broadcast the location has of its own ("none" = the one of its group)
const broadcastKindOf = (location, broadcasts) => {
  const own = location?.broadcastId ? broadcasts.find(b => b.id === location.broadcastId) || location.broadcast : null
  if (!location?.broadcastId) return 'none'
  return own && isCompositeBroadcast(own) ? 'composite' : 'regular'
}

// `location` edits an existing location. `template` (a location to copy, see
// lib/membership.js#locationTemplate) opens the CREATE form with all of its values filled in and
// only the name left empty.
export default function LocationModal({ location, template, onChange }) {

  const toast = useToast()

  const { user } = useContext(AuthContext)

  const { closeDrawer } = useContext(DrawerContext)

  const [tabIndex, setTabIndex] = useState(0)
  const handleTabsChange = (index) => {
    setTabIndex(index)
  }
  
  const [data, setData] = useState(() => template ? locationTemplate(template) : location || {
    name: '',
    description: '',
    address: '',
    city: '',
    country: '',
    telephone: '',
    workingHours: '',
    groupId: null,
    group: {
      name: '',
      broadcastId: null,
      broadcast: {
        name: '',
        type: 'regular'
      },
      layoutColor: null,
    },
    broadcastId: null,
    broadcast: {
      name: '',
      type: 'regular'
    },
    layoutWidth: null,
    layoutHeight: null,
    layoutOffsetX: null,
    layoutOffsetY: null
  })

  const [groups] = useGroup({user})
  const [broadcasts] = useBroadcast({user})
  const [allContent] = useContent({user})

  // A copy starts with the same kind of broadcast as the location it was copied from
  const [broadcastType, setBroadcastType] = useState(() => template ? broadcastKindOf(template, broadcasts) : 'none')


  useEffect(() => {
    if (location) {
      setData(location || {})
      if (location.broadcast) {
        if (location.broadcast.broadcastList) {
          setBroadcastType('composite')
        } else {
          setBroadcastType('regular')
        }
      }
    }
  }, [location])

  const toggleBroadcastType = (type) => {
    setBroadcastType(type)
    setData({...data, broadcastId: null})
  }

  const getGeo = async () => {
    return new Promise((resolve, reject) => {
      const address = [data.address, data.city, data.country].filter(el => !!el).join(', ')
      geocoder.geocode({ address: address }, function (results) {
        if (results && results.length > 0) {
          resolve({
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng()
          })
        } else {
          reject("Can't find location info for this address")
        }
      });
    })
  }

  // Creates a broadcast and returns it (with its new id). Errors propagate to
  // saveLocation, which reports them in a single try/catch.
  const createBroadcast = async (br) => {
    return createBroadcastRecord({
      ...br,
      clientId: user.clientId,
      updated_at: moment().toISOString()
    })
  }

  // Creates the group entered in the "Group" tab and returns it. A broadcast
  // chosen as "new" inside the group form is created first.
  const createGroup = async (grp) => {
    const { broadcast, ...payload } = grp
    if (payload.broadcastId?.startsWith('new')) {
      const createdBroadcast = await createBroadcast(broadcast)
      payload.broadcastId = createdBroadcast.id
    }
    payload.clientId = user.clientId
    payload.updated_at = moment().toISOString()
    return create('group', payload)
  }

  const saveLocation = async () => {
    // A copy starts with an empty name; without one it could not be told apart from the original
    if (template && !data.name?.trim()) {
      toast({ title: 'Error!', description: 'Enter a name for the new location.', status: 'error', duration: null, isClosable: true })
      return
    }
    if (!data.groupId) {
      toast({ title: 'Error!', description: 'Location needs to have a group!', status: 'error', duration: null, isClosable: true })
      return
    }

    try {
      // Work on a copy without the denormalised `group` / `broadcast` objects,
      // so the form state stays intact if anything below fails.
      const { group, broadcast, ...payload } = data
      payload.clientId = user.clientId
      payload.updated_at = moment().toISOString()

      // Geocode first: a bad address then fails before anything is created.
      const geo = await getGeo()
      payload.latitude = geo.latitude
      payload.longitude = geo.longitude

      if (payload.broadcastId?.startsWith('new')) {
        const createdBroadcast = await createBroadcast(broadcast)
        payload.broadcastId = createdBroadcast.id
      }

      if (payload.groupId.startsWith('new')) {
        if (!group) {
          throw new Error('Group details are missing!')
        }
        const createdGroup = await createGroup(group)
        payload.groupId = createdGroup.id
      }

      // Decided before writing: `create` puts the new id on the payload, which made a new
      // location report "updated"
      const isNew = !payload.id
      if (isNew) {
        await create('location', payload)
      } else {
        await save('location', location.id, payload)
      }
      onChange && onChange()
      closeDrawer()
      toast({ title: `Location ${isNew ? 'created' : 'updated'}`, status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message || err, status: 'error', duration: null, isClosable: true })
    }
  }

  return (
    <>
      <DrawerBody>
        <Stack direction='column' spacing={4}>
          <FormControl>
            <FormLabel>ID</FormLabel>
            <Input type='text' value={data.id || 'ID will be auto-generated'} disabled />
          </FormControl>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input type='text' value={data.name} onChange={e => setData({...data, name: e.target.value })} />
          </FormControl>
          <Tabs colorScheme='brand' variant='enclosed' index={tabIndex} onChange={handleTabsChange}>
            <TabList borderColor={'#f0f6ff'}>
              <Tab border={'none'} _focus={{ boxShadow: "none" }} _selected={{ background: '#f0f6ff'}} _hover={{background: '#fafcff'}}>Details</Tab>
              <Tab border={'none'} _focus={{ boxShadow: "none" }} _selected={{ background: '#f0f6ff'}} _hover={{background: '#fafcff'}}>Group</Tab>
              <Tab border={'none'} _focus={{ boxShadow: "none" }} _selected={{ background: '#f0f6ff'}} _hover={{background: '#fafcff'}}>Broadcast</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>  
                <Stack direction='column' spacing={4}>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Input type='text' value={data.description} onChange={e => setData({...data, description: e.target.value })} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Address</FormLabel>
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'stretch', md: 'center' }}>
                      <Input w={{ base: '100%', md: '50%' }} type='text' placeholder="Street and number" value={data.address} onChange={e => setData({...data, address: e.target.value })} />
                      <Input w={{ base: '100%', md: '25%' }} type='text' placeholder="City" value={data.city} onChange={e => setData({...data, city: e.target.value })} />
                      <Input w={{ base: '100%', md: '25%' }} type='text' placeholder="Country" value={data.country} onChange={e => setData({...data, country: e.target.value })} />
                    </Stack>
                  </FormControl>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'stretch', md: 'center' }}>
                    <FormControl>
                      <FormLabel>Telephone</FormLabel>
                      <Input type='text' placeholder="#" value={data.telephone} onChange={e => setData({...data, telephone: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Working hours</FormLabel>
                      <Input type='text' placeholder="08-20h" value={data.workingHours} onChange={e => setData({...data, workingHours: e.target.value })} />
                    </FormControl>
                  </Stack>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'stretch', md: 'center' }}>
                    <FormControl>
                      <FormLabel>Resolution</FormLabel>
                      <Stack direction='row' spacing={2} align='center'>
                        <Input type='number' placeholder="Width" value={data.layoutWidth} onChange={e => setData({...data, layoutWidth: Number(e.target.value) })} />
                        <Input type='number' placeholder="Height" value={data.layoutHeight} onChange={e => setData({...data, layoutHeight: Number(e.target.value) })} />
                      </Stack>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Offset</FormLabel>
                      <Stack direction='row' spacing={2} align='center'>
                        <Input type='number' placeholder="X" value={data.layoutOffsetX} onChange={e => setData({...data, layoutOffsetX: Number(e.target.value) })} />
                        <Input type='number' placeholder="Y" value={data.layoutOffsetY} onChange={e => setData({...data, layoutOffsetY: Number(e.target.value) })} />
                      </Stack>
                    </FormControl>
                  </Stack>
                  {data.id && (
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'stretch', md: 'center' }}>
                      <FormControl>
                        <FormLabel>Last ping</FormLabel>
                        <LastSeen value={data.lastPing} />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Last sync</FormLabel>
                        <LastSeen value={data.lastSync} />
                      </FormControl>
                    </Stack>
                  )}
                </Stack>
              </TabPanel>
              <TabPanel px={0}>  
                <Stack direction='column' spacing={4}>
                  <SearchSelect placeholder='Select group'
                    pinned={[{ value: 'new-group', label: 'Create new group' }]}
                    options={groups.map(g => ({ value: g.id, label: g.name }))}
                    value={data.groupId}
                    onChange={groupId => setData({...data, groupId})} />
                  {data.groupId === 'new-group' && (
                    <GroupForm onChange={group => setData({...data, group: group })} />
                  )}
                </Stack>
              </TabPanel>
              <TabPanel px={0}>  
                <FormControl>
                  <Stack direction='column' spacing={4} align='center'>
                    <FormControl display='flex' alignItems='center'>
                      <RadioGroup onChange={toggleBroadcastType} value={broadcastType}>
                        <Stack direction='row' spacing={4}>
                          <Radio value='none'>From group</Radio>
                          <Radio value='composite'>Composite broadcast</Radio>
                          <Radio value='regular'>Regular broadcast</Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                    {broadcastType === 'composite' && (
                      <SearchSelect placeholder='Select composite broadcast'
                        pinned={[{ value: 'new-composite-broadcast', label: 'Create new composite broadcast' }]}
                        options={broadcasts.filter(b => !!b.broadcastList).map(b => ({ value: b.id, label: b.name }))}
                        value={data.broadcastId}
                        onChange={broadcastId => setData({...data, broadcastId})} />
                    )}
                    {broadcastType === 'regular' && (
                      <SearchSelect placeholder='Select regular broadcast'
                        pinned={[{ value: 'new-regular-broadcast', label: 'Create new regular broadcast' }]}
                        options={broadcasts.filter(b => !b.broadcastList).map(b => ({ value: b.id, label: b.name }))}
                        value={data.broadcastId}
                        onChange={broadcastId => setData({...data, broadcastId})} />
                    )}
                  </Stack>
                    {broadcastType !== 'none' && data.broadcastId?.startsWith('new') && (
                      <Stack direction='column' spacing={0} mt={4}>
                        <FormControl>
                          <FormLabel>Name</FormLabel>
                          <Input type='text' value={data.broadcast?.name} onChange={e => setData({...data, broadcast: {...data.broadcast, name: e.target.value }})} />
                        </FormControl>
                        {data.broadcastId === 'new-regular-broadcast' && (
                          <>
                            <FormControl>
                              <FormLabel>Resolutions</FormLabel>
                              <ResolutionPicker value={data.broadcast?.resolutions} content={allContent} onChange={resolutions => setData({...data, broadcast: {...data.broadcast, resolutions }})} />
                            </FormControl>
                            <ContentSelection resolutions={data.broadcast?.resolutions} selected={data.broadcast?.contentList || []} onChange={({items, duration}) => setData(current => ({...current, broadcast: {...current.broadcast, type: 'regular', contentList: items, duration: duration, broadcastList: null }}))} />
                          </>
                        )}
                        {data.broadcastId === 'new-composite-broadcast' && (
                          <BroadcastSelection selected={data.broadcast?.broadcastList || []} onChange={({items, duration}) => setData({...data, broadcast: {...data.broadcast, type: 'composite', broadcastList: items, duration: duration, contentList: null }})} />
                        )}
                      </Stack>
                    )}
                </FormControl>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => saveLocation()}>
          {location ? 'Save' : (template ? 'Create copy' : 'Create')}
        </Button>
      </DrawerFooter>
    </>
  );
}